import { useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import {
  emptyCard, varNames, type CardButtonShape, type CardDraft, type CardMedia, type OfferDraft, type TemplateFormat,
} from '@/lib/template-rich';

// Formatos ricos de MARKETING no formulário: a oferta por tempo limitado (um selo
// e, se quiser, contagem regressiva) e o carrossel (2 a 10 cartões). A Meta exige
// que todos os cartões tenham a mesma mídia e os mesmos botões — então a tela pede
// os botões UMA vez, para todos, e em cada cartão só o que muda: a mídia, o texto e
// o link. É a regra dela virando menos trabalho, não mais.

export function FormatPicker({ format, onChange, category }: {
  format: TemplateFormat; onChange: (f: TemplateFormat) => void; category: string;
}) {
  const t = useT();
  const allowed: TemplateFormat[] = category === 'MARKETING' ? ['standard', 'offer', 'carousel']
    : category === 'UTILITY' ? ['standard', 'carousel'] : ['standard'];
  if (allowed.length === 1) return null;
  return (
    <div>
      <Label>{t('templates.rich.format')}</Label>
      <div className="mt-1 flex flex-wrap gap-2">
        {allowed.map((f) => (
          <button key={f} type="button" onClick={() => onChange(f)}
            className={cn('rounded-full border px-3 py-1 text-sm transition-colors',
              format === f ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:text-foreground')}>
            {t(`templates.rich.formats.${f}`)}
          </button>
        ))}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{t(`templates.rich.formatHint.${format}`)}</p>
    </div>
  );
}

export function OfferFields({ value, onChange }: { value: OfferDraft; onChange: (v: OfferDraft) => void }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div>
        <Label htmlFor="tpl-offer">{t('templates.rich.offerText')}</Label>
        <Input id="tpl-offer" value={value.text} maxLength={16} placeholder={t('templates.rich.offerPlaceholder')}
          onChange={(e) => onChange({ ...value, text: e.target.value })} />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={value.has_expiration}
          onChange={(e) => onChange({ ...value, has_expiration: e.target.checked })} />
        <span>{t('templates.rich.offerExpiration')}</span>
      </label>
      <p className="text-xs text-muted-foreground">{t('templates.rich.offerRules')}</p>
    </div>
  );
}

function uploadMedia(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch('POST', '/templates/media', fd).then((r) => {
    if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? 'upload');
    return (r.data as { url: string }).url;
  });
}

export function CarouselEditor({ media, setMedia, shape, setShape, cards, setCards }: {
  media: CardMedia; setMedia: (m: CardMedia) => void;
  shape: CardButtonShape[]; setShape: (s: CardButtonShape[]) => void;
  cards: CardDraft[]; setCards: (c: CardDraft[]) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState<number | null>(null);
  const patch = (i: number, p: Partial<CardDraft>) => setCards(cards.map((c, j) => (j === i ? { ...c, ...p } : c)));

  async function pick(i: number, f: File) {
    setBusy(i);
    try { patch(i, { media_url: await uploadMedia(f) }); } catch { toast.error(t('templates.field.uploadFailed')); } finally { setBusy(null); }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Label>{t('templates.rich.cardMedia')}</Label>
        <div className="mt-1 flex gap-2">
          {(['image', 'video'] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMedia(m)}
              className={cn('rounded-full border px-3 py-1 text-sm', media === m ? 'border-primary bg-primary/10' : 'border-input text-muted-foreground')}>
              {t(`templates.header.${m}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Botões: uma vez, valem para todos os cartões (regra da Meta). */}
      <div>
        <Label>{t('templates.rich.cardButtons')}</Label>
        <p className="mb-2 text-xs text-muted-foreground">{t('templates.rich.cardButtonsHint')}</p>
        <div className="flex flex-col gap-2">
          {shape.map((b, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-muted px-2 py-1 text-xs">{t(`templates.buttons.kind.${b.type}`)}</span>
              <Input className="w-40" value={b.text} maxLength={25} placeholder={t('templates.buttons.label')}
                onChange={(e) => setShape(shape.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
              {b.type === 'phone_number' && (
                <Input className="w-44" value={b.phone ?? ''} placeholder="+5511999999999"
                  onChange={(e) => setShape(shape.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
              )}
              <Button size="sm" variant="ghost" onClick={() => setShape(shape.filter((_, j) => j !== i))}><Trash2 className="size-3.5" /></Button>
            </div>
          ))}
          {shape.length < 2 && (
            <div className="flex flex-wrap gap-2">
              {(['quick_reply', 'url', 'phone_number'] as const).map((k) => (
                <Button key={k} size="sm" variant="outline" disabled={k === 'phone_number' && shape.some((x) => x.type === 'phone_number')}
                  onClick={() => setShape([...shape, { type: k, text: '' }])}>
                  <Plus className="size-3.5" />{t(`templates.buttons.kind.${k}`)}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {cards.map((c, i) => {
        const vars = varNames(c.body);
        return (
          <div key={i} className="flex flex-col gap-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('templates.rich.card', { n: i + 1 })}</span>
              {cards.length > 2 && (
                <Button size="sm" variant="ghost" onClick={() => setCards(cards.filter((_, j) => j !== i))}><Trash2 className="size-3.5" /></Button>
              )}
            </div>
            <label className="inline-flex w-fit">
              <input type="file" className="hidden" accept={media === 'image' ? 'image/jpeg,image/png' : 'video/mp4'}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pick(i, f); }} />
              <span className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted">
                {busy === i && <Loader2 className="size-4 animate-spin" />}
                {c.media_url ? t('templates.field.mediaReplace') : t('templates.field.mediaUpload')}
              </span>
            </label>
            <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" rows={2} maxLength={160}
              value={c.body} placeholder={t('templates.rich.cardBodyPlaceholder')} onChange={(e) => patch(i, { body: e.target.value })} />
            {vars.map((n, k) => (
              <div key={n} className="flex items-center gap-2">
                <code className="min-w-20 shrink-0 rounded bg-muted px-2 py-1 text-xs">{`{{${n}}}`}</code>
                <Input value={c.examples[k] ?? ''} placeholder={t('templates.field.examplePlaceholder')}
                  onChange={(e) => { const ex = [...c.examples]; ex[k] = e.target.value; patch(i, { examples: ex }); }} />
              </div>
            ))}
            {shape.map((b, k) => b.type === 'url' && (
              <div key={k} className="flex flex-col gap-1">
                <Input value={c.urls[k] ?? ''} placeholder={t('templates.rich.cardUrl', { button: b.text || t('templates.buttons.kind.url') })}
                  onChange={(e) => { const u = [...c.urls]; u[k] = e.target.value; patch(i, { urls: u }); }} />
                {(c.urls[k] ?? '').includes('{{') && (
                  <Input value={c.url_examples[k] ?? ''} placeholder={t('templates.buttons.urlExample')}
                    onChange={(e) => { const u = [...c.url_examples]; u[k] = e.target.value; patch(i, { url_examples: u }); }} />
                )}
              </div>
            ))}
          </div>
        );
      })}
      {cards.length < 10 && (
        <Button variant="outline" onClick={() => setCards([...cards, emptyCard()])}>
          <Plus className="size-4" />{t('templates.rich.addCard')}
        </Button>
      )}
    </div>
  );
}

/** Prévia do carrossel: a abertura e os cartões lado a lado, como no celular. */
export function CarouselPreview({ body, media, shape, cards }: {
  body: string; media: CardMedia; shape: CardButtonShape[]; cards: CardDraft[];
}) {
  const t = useT();
  const [at, setAt] = useState(0);
  const c = cards[Math.min(at, cards.length - 1)];
  return (
    <div className="rounded-lg bg-[#e5ddd5] p-4 dark:bg-[#0b141a]">
      <div className="mb-2 max-w-sm rounded-lg rounded-tl-none bg-white p-2 text-sm shadow-sm dark:bg-[#202c33]">
        {body || <span className="text-muted-foreground">{t('templates.preview.emptyBody')}</span>}
      </div>
      {c && (
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" disabled={at === 0} onClick={() => setAt(at - 1)} aria-label="‹"><ChevronLeft className="size-4" /></Button>
          <div className="w-56 overflow-hidden rounded-lg bg-white shadow-sm dark:bg-[#202c33]">
            {c.media_url && media === 'image'
              ? <img src={c.media_url} alt="" className="h-28 w-full object-cover" />
              : <div className="flex h-28 items-center justify-center bg-black/5 text-xs text-muted-foreground">{media === 'video' ? t('templates.preview.video') : t('templates.preview.noMedia')}</div>}
            <p className="p-2 text-sm">{c.body}</p>
            {shape.map((b, i) => (
              <div key={i} className="border-t py-1.5 text-center text-sm text-sky-600">{b.text || t(`templates.buttons.kind.${b.type}`)}</div>
            ))}
          </div>
          <Button size="icon" variant="ghost" disabled={at >= cards.length - 1} onClick={() => setAt(at + 1)} aria-label="›"><ChevronRight className="size-4" /></Button>
        </div>
      )}
      <p className="mt-2 text-center text-xs text-muted-foreground">{t('templates.rich.cardOf', { n: Math.min(at, cards.length - 1) + 1, total: cards.length })}</p>
    </div>
  );
}
